import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Seo from "@/components/Seo";
import PasswordField from "@/components/PasswordField";
import { registerCustomer } from "@/lib/customerAuth";

/**
 * /account/register (Design Specification Section 12.3). The server answers identically for a new and an
 * already-registered email (AC-ACCT-2), so this page shows one success screen regardless — it never says
 * "That email is already registered."
 */
export default function Register() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 10) {
      setError("Password must be at least 10 characters.");
      return;
    }
    setSubmitting(true);
    try {
      await registerCustomer(email.trim(), password);
      setDone(true);
    } catch (err) {
      const status = err && err.response && err.response.status;
      if (status === 429) setError("Too many attempts. Please try again later.");
      else if (status === 422) setError("Enter a valid email and a password of at least 10 characters.");
      else setError("We could not create your account right now. Please try again later.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="max-w-[480px] mx-auto px-6 py-16" data-testid="account-register-page">
        <Seo title="Create an account" noindex path="/account/register" />
        <h1 tabIndex={-1} className="font-display text-3xl font-extrabold tracking-tight text-[#0A2540] mb-4" data-testid="register-success-heading">
          Check your email.
        </h1>
        <p role="status" className="text-slate-700" data-testid="register-success">
          If this address is new, we sent a code to confirm it.
        </p>
        <button type="button" onClick={() => navigate("/account/verify", { state: { email: email.trim() } })} className="btn-green mt-6" data-testid="register-go-verify">
          Enter my code
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-[480px] mx-auto px-6 py-16" data-testid="account-register-page">
      <Seo title="Create an account" noindex path="/account/register" />
      <h1 className="font-display text-3xl font-extrabold tracking-tight text-[#0A2540] mb-6">Create an account</h1>

      {error && (
        <p role="alert" className="border-2 border-[#B91C1C] bg-[#FEF2F2] text-[#B91C1C] rounded-xl p-3 mb-5" data-testid="register-error">
          {error}
        </p>
      )}

      <form onSubmit={submit} noValidate className="space-y-5" data-testid="register-form">
        <div>
          <label htmlFor="register-email" className="text-overline text-zinc-500 mb-1 block">Email</label>
          <input
            id="register-email"
            type="email"
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="input-brutal"
            data-testid="field-email"
          />
        </div>
        <PasswordField
          id="register-password"
          label="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="new-password"
          hint="At least 10 characters."
          required
          maxLength={72}
        />
        <button type="submit" disabled={submitting} aria-busy={submitting} className="btn-green w-full" data-testid="register-submit">
          {submitting ? "Creating account…" : "Create account"}
        </button>
      </form>

      <p className="mt-6 text-sm text-slate-600">
        Already have an account? <Link to="/account/login" className="underline">Sign in</Link>
      </p>
    </div>
  );
}
